import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Smile, CircleDot, BookOpen, Utensils, CheckCircle,
  ChevronRight, Activity
} from "lucide-react";

// Import existing pages as components
import MoodPage from "./MoodPage";
import NeedsPage from "./NeedsPage";
import DiaryPage from "./DiaryPage";
import NutritionPage from "./NutritionPage";
import HabitsPage from "./HabitsPage";
import VoiceMoodPage from "./VoiceMoodPage";
import { BiweeklyReminderCard } from "@/components/biweekly/BiweeklyAssessment";

const SECTIONS = [
  { id: "checkin-benessere", label: "Check-in", icon: Activity, description: "Descrivi un momento — wizard guidato in 4 passi", color: "from-rose-500 to-pink-600" },
  { id: "umore", label: "Umore", icon: Smile, description: "Come ti senti oggi?", color: "from-yellow-400 to-amber-500" },
  { id: "bisogni", label: "Bisogni", icon: CircleDot, description: "La ruota dei tuoi bisogni", color: "from-violet-400 to-purple-500" },
  { id: "diario", label: "Diario", icon: BookOpen, description: "Scrivi i tuoi pensieri", color: "from-cyan-400 to-blue-500" },
  // Nutrition tile removed from Benessere hub (2026-07-10): non è core rispetto
  // alla missione psicologica. Resta accessibile via Impostazioni → Extra e via
  // URL diretto `/benessere?section=nutrizione` per chi la usa già.
  { id: "abitudini", label: "Abitudini", icon: CheckCircle, description: "Routine e obiettivi", color: "from-indigo-400 to-blue-500" },
];

const BackButton = ({ onClick }) => (
  <Button
    variant="ghost"
    size="sm"
    onClick={onClick}
    className="mb-4"
    data-testid="benessere-back-button"
  >
    ← Torna a Benessere
  </Button>
);

const BenesserePage = () => {
  // 2026-02-16 (Laura): la sezione attiva È nella URL come query param `?section=...`.
  // Al reload, l'URL preserva lo stato. Non azzeriamo più i searchParams dopo
  // la lettura — era proprio quello che causava il "ritorno all'hub" al refresh.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = searchParams.get("section");
  const initialTab = searchParams.get("tab");

  const openSection = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("section", id);
    setSearchParams(next, { replace: false });
  };

  const closeSection = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("section");
    next.delete("tab");
    setSearchParams(next, { replace: false });
  };

  // Legacy redirect: alcune URL pre-2026 usavano "checkin-vocale" come slug
  useEffect(() => {
    if (activeSection === "checkin-vocale") {
      const next = new URLSearchParams(searchParams);
      next.set("section", "checkin-benessere");
      setSearchParams(next, { replace: true });
    }
  }, [activeSection, searchParams, setSearchParams]);

  // If a section is active, show that page
  if (activeSection) {
    switch (activeSection) {
      case "checkin-benessere":
      case "checkin-vocale":  // Legacy: rediretto via useEffect sopra
        return (
          <div>
            <BackButton onClick={closeSection} />
            <VoiceMoodPage initialTab={initialTab} />
          </div>
        );
      case "umore":
        return (
          <div>
            <BackButton onClick={closeSection} />
            <MoodPage />
          </div>
        );
      case "bisogni":
        return (
          <div>
            <BackButton onClick={closeSection} />
            <NeedsPage />
          </div>
        );
      case "diario":
        return (
          <div>
            <BackButton onClick={closeSection} />
            <DiaryPage />
          </div>
        );
      case "nutrizione":
        return (
          <div>
            <BackButton onClick={closeSection} />
            <NutritionPage />
          </div>
        );
      case "abitudini":
        return (
          <div>
            <BackButton onClick={closeSection} />
            <HabitsPage />
          </div>
        );
      default:
        return null;
    }
  }

  // Hub view
  return (
    <div className="space-y-5 animate-fade-in pb-24 md:pb-8" data-testid="benessere-page">
      {/* Header */}
      <div>
        <h1 className="page-title">Benessere</h1>
        <p className="text-muted-foreground">
          Ascoltati, osserva i tuoi schemi, prenditi cura di te
        </p>
      </div>

      {/* Biweekly assessment reminder (auto-hidden if not due) */}
      <BiweeklyReminderCard />

      {/* Section Cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {SECTIONS.map(({ id, label, icon: Icon, description, color }) => (
          <Card 
            key={id}
            className="border shadow-sm hover:shadow-md transition-all cursor-pointer group overflow-hidden"
            onClick={() => openSection(id)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold flex items-center gap-1 text-[15px]">
                    {label}
                    <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </h3>
                  <p className="text-sm text-muted-foreground truncate">{description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default BenesserePage;
